/** Request account deletion on a subscriber's behalf (TMMPDM-0013). */
import { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { SubmitButton } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { ApiError, errorCopy, isValidationFailed, validationFields } from '@/api/errors';
import type { AdminSubscriptionRow } from '@/api/tmm/types';

interface RequestDeletionDialogProps {
  open: boolean;
  slug: string;
  onClose: () => void;
  onDone: () => void;
}

export function RequestDeletionDialog({ open, slug, onClose, onDone }: RequestDeletionDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      {open ? (
        <RequestDeletionForm key={slug} slug={slug} onClose={onClose} onDone={onDone} />
      ) : null}
    </Dialog>
  );
}

function optionLabel(row: AdminSubscriptionRow): string {
  return row.displayName ?? row.email;
}

function RequestDeletionForm({ slug, onClose, onDone }: Omit<RequestDeletionDialogProps, 'open'>) {
  const { t } = useCopy();
  const [rows, setRows] = useState<AdminSubscriptionRow[]>([]);
  const [selected, setSelected] = useState<AdminSubscriptionRow | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [emailQuery, setEmailQuery] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const trimmed = searchInput.trim();
      setEmailQuery(trimmed.length >= 2 ? trimmed : '');
    }, 250);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .listSubscriptions(slug, emailQuery ? { email: emailQuery } : undefined)
      .then((list) => {
        if (!cancelled) setRows(list.subscriptions);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, emailQuery]);

  const submit = async () => {
    const nextFields: Record<string, string> = {};
    if (!selected) {
      nextFields.userId = t('deletions.requestDialog.personRequired', 'Person is required.');
    }
    const trimmed = reason.trim();
    if (!trimmed) {
      nextFields.reason = t('deletions.requestDialog.reasonRequired', 'Reason is required.');
    }
    if (Object.keys(nextFields).length > 0) {
      setFieldErrors(nextFields);
      return;
    }
    if (!selected) return;
    setSubmitting(true);
    setFormError(null);
    setFieldErrors({});
    try {
      await adminApi.requestDeletion(slug, { userId: selected.userId, reason: trimmed });
      onDone();
    } catch (error: unknown) {
      if (error instanceof ApiError && isValidationFailed(error)) {
        const fields = validationFields(error, t);
        setFieldErrors(fields);
        // A field the form doesn't show is said once, in the banner; shown fields are said at the field.
        if (Object.keys(fields).some((field) => !['userId', 'reason'].includes(field))) {
          setFormError(
            errorCopy(error, t, t('deletions.request.error', "Couldn't request the deletion.")),
          );
        }
      } else {
        setFormError(
          errorCopy(error, t, t('deletions.request.error', "Couldn't request the deletion.")),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogTitle>{t('deletions.requestDialog.title', 'Request deletion')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {formError ? <Alert severity="error">{formError}</Alert> : null}
          <Alert severity="warning">
            {t(
              'deletions.request.warning',
              'Everything this person owns is erased after the cooling-off period. They are emailed and can cancel.',
            )}
          </Alert>
          <Autocomplete
            options={rows}
            value={selected}
            onChange={(_event, next) => setSelected(next)}
            onInputChange={(_event, next, reasonCode) => {
              if (reasonCode === 'input') setSearchInput(next);
              if (reasonCode === 'clear') setSearchInput('');
            }}
            filterOptions={(options) => options}
            getOptionLabel={optionLabel}
            getOptionKey={(option) => option.userId}
            isOptionEqualToValue={(a, b) => a.userId === b.userId}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography>{option.displayName ?? option.email}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.email}
                  </Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('deletions.requestDialog.person', 'Person')}
                required
                error={Boolean(fieldErrors.userId)}
                helperText={fieldErrors.userId}
              />
            )}
          />
          <TextField
            label={t('deletions.requestDialog.reason', 'Reason')}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            error={Boolean(fieldErrors.reason)}
            helperText={
              fieldErrors.reason ?? t('deletions.counter', '{{n}}/200', { n: reason.length })
            }
            required
            multiline
            minRows={2}
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('deletions.requestDialog.cancel', 'Cancel')}</Button>
        <SubmitButton onClick={() => void submit()} busy={submitting}>
          {t('deletions.requestDialog.submit', 'Request')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}
